import { PoiDataService } from '../../services/PoiDataService';
import store from '../../state';
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

export class AqiStationModal {
  private container: HTMLElement;
  private aqiChart: Chart | null = null;
  private smokeChart: Chart | null = null;

  constructor() {
    this.container = document.createElement('div');
    this.container.id = 'aqi-station-modal';
    this.container.style.cssText = `
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background-color: rgba(0, 0, 0, 0.75);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      z-index: 99999;
      align-items: center;
      justify-content: center;
      padding: 16px;
    `;

    this.container.addEventListener('click', (e) => {
      if (e.target === this.container) {
        this.close();
      }
    });

    document.body.appendChild(this.container);
  }

  public close() {
    this.container.style.display = 'none';
    this.container.innerHTML = '';
    if (this.aqiChart) {
      this.aqiChart.destroy();
      this.aqiChart = null;
    }
    if (this.smokeChart) {
      this.smokeChart.destroy();
      this.smokeChart = null;
    }
  }

  public async open(props: any, lat: number, lon: number) {
    this.close();
    this.container.style.display = 'flex';

    const name = props.name || 'Air Quality Monitoring Station';
    const id = props.id || '';
    const src = props.src || 'Station';
    const aqi = props.aqi !== undefined && props.aqi !== null ? props.aqi : null;
    const aqhi = props.aqhi !== undefined && props.aqhi !== null ? props.aqhi : null;
    const param = props.param || '';
    const cat = props.cat || 'Unknown';
    const ts = props.ts || '';

    const isAirNow = src === 'AIRNOW' || (aqi !== null && aqi !== undefined);
    const scoreVal = isAirNow ? (aqi !== null && aqi !== undefined ? aqi : 'N/A') : (aqhi !== null && aqhi !== undefined ? aqhi : 'N/A');
    const scoreUnit = isAirNow ? 'AQI' : 'AQHI';
    const srcLabel = isAirNow ? '🇺🇸 US AirNow (EPA)' : '🇨🇦 Canada ECCC (AQHI)';

    let catBg = '#00e400';
    let catFg = '#000000';

    if (isAirNow) {
      if (typeof aqi === 'number') {
        if (aqi <= 50) { catBg = '#00e400'; catFg = '#000'; }
        else if (aqi <= 100) { catBg = '#ffff00'; catFg = '#000'; }
        else if (aqi <= 150) { catBg = '#ff7e00'; catFg = '#fff'; }
        else if (aqi <= 200) { catBg = '#ff0000'; catFg = '#fff'; }
        else if (aqi <= 300) { catBg = '#8f3f97'; catFg = '#fff'; }
        else { catBg = '#7e0023'; catFg = '#fff'; }
      }
    } else {
      if (typeof aqhi === 'number') {
        if (aqhi <= 3) { catBg = '#00ccff'; catFg = '#000'; }
        else if (aqhi <= 6) { catBg = '#ffcc00'; catFg = '#000'; }
        else if (aqhi <= 10) { catBg = '#ff6600'; catFg = '#fff'; }
        else { catBg = '#990000'; catFg = '#fff'; }
      }
    }

    let formattedTs = ts;
    try {
      const d = new Date(ts);
      if (!isNaN(d.getTime())) {
        formattedTs = d.toISOString().replace('T', ' ').substring(0, 16) + ' UTC';
      }
    } catch (e) {}

    const card = document.createElement('div');
    card.style.cssText = `
      width: 100%;
      max-width: 680px;
      max-height: 90vh;
      background: rgba(18, 22, 30, 0.96);
      border: 1px solid var(--border-light);
      border-radius: 16px;
      box-shadow: 0 20px 50px rgba(0, 0, 0, 0.8);
      display: flex;
      flex-direction: column;
      overflow: hidden;
    `;

    card.innerHTML = `
      <div style="display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; border-bottom: 1px solid var(--border-light); background: rgba(255, 255, 255, 0.03);">
        <div>
          <div style="font-size: 1.1rem; font-weight: 700; color: #fff;">${name}</div>
          <div style="font-size: 0.78rem; color: var(--text-muted); margin-top: 2px;">Station ID: ${id} • ${srcLabel} • ${lat.toFixed(4)}°, ${lon.toFixed(4)}°</div>
        </div>
        <button id="aqi-modal-close" style="background: rgba(255, 255, 255, 0.08); border: none; color: #fff; width: 32px; height: 32px; border-radius: 50%; cursor: pointer; font-size: 16px; display: flex; align-items: center; justify-content: center;">✕</button>
      </div>

      <div style="padding: 20px; overflow-y: auto; display: flex; flex-direction: column; gap: 16px;">
        <!-- Live Station Reading Telemetry Header Card -->
        <div style="display: flex; align-items: center; justify-content: space-between; padding: 14px 18px; background: rgba(255, 255, 255, 0.04); border: 1px solid var(--border-light); border-radius: 12px; gap: 12px; flex-wrap: wrap;">
          <div style="display: flex; flex-direction: column; gap: 2px;">
            <div style="font-size: 0.72rem; font-weight: 700; text-transform: uppercase; color: var(--text-muted); letter-spacing: 0.5px;">Live Observation Reading</div>
            <div style="display: flex; align-items: baseline; gap: 6px;">
              <span style="font-size: 2.2rem; font-weight: 900; color: ${catBg}; line-height: 1;">${scoreVal}</span>
              <span style="font-size: 1rem; font-weight: 700; color: #f8fafc;">${scoreUnit} ${param ? `(${param})` : ''}</span>
            </div>
            <div style="font-size: 0.72rem; color: #a1a1aa;">🕒 Observed: ${formattedTs}</div>
          </div>
          <div style="display: flex; flex-direction: column; align-items: flex-end; gap: 6px;">
            <span style="background: ${catBg}; color: ${catFg}; font-weight: 800; font-size: 0.88rem; padding: 6px 14px; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.4); text-transform: uppercase;">
              ${cat}
            </span>
            <div style="font-size: 0.72rem; color: var(--text-muted);">Coordinates: ${lat.toFixed(4)}, ${lon.toFixed(4)}</div>
          </div>
        </div>

        <!-- AQI Forecast Chart Card -->
        <div style="background: rgba(255, 255, 255, 0.03); border: 1px solid var(--border-light); border-radius: 12px; padding: 16px; display: flex; flex-direction: column; gap: 10px;">
          <div style="display: flex; align-items: center; justify-content: space-between;">
            <div style="font-size: 0.85rem; font-weight: 700; color: #fff; text-transform: uppercase; letter-spacing: 0.5px; display: flex; align-items: center; gap: 6px;">
              <span>🫁</span> <span>Air Quality Index (AQI) Forecast Trend</span>
            </div>
            <span id="aqi-chart-status" style="font-size: 0.72rem; color: var(--text-muted);">Loading model trend...</span>
          </div>
          <div style="position: relative; width: 100%; height: 180px;">
            <canvas id="aqiStationAqiChart"></canvas>
          </div>
        </div>

        <!-- Smoke Density Forecast Chart Card -->
        <div style="background: rgba(255, 255, 255, 0.03); border: 1px solid var(--border-light); border-radius: 12px; padding: 16px; display: flex; flex-direction: column; gap: 10px;">
          <div style="display: flex; align-items: center; justify-content: space-between;">
            <div style="font-size: 0.85rem; font-weight: 700; color: #fff; text-transform: uppercase; letter-spacing: 0.5px; display: flex; align-items: center; gap: 6px;">
              <span>🌫️</span> <span>Smoke Concentration Forecast Trend</span>
            </div>
            <span id="smoke-chart-status" style="font-size: 0.72rem; color: var(--text-muted);">Loading Smoke model trend...</span>
          </div>
          <div style="position: relative; width: 100%; height: 180px;">
            <canvas id="aqiStationSmokeChart"></canvas>
          </div>
        </div>
      </div>
    `;

    this.container.appendChild(card);

    const closeBtn = card.querySelector('#aqi-modal-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.close());
    }

    // Fetch Forecast Trend Data
    try {
      const state = store.getState();
      const model = state.selectedModels['aqi'] || 'gefs';
      const todayStr = PoiDataService.getTodayLocalDateStr(lat, lon);
      const forecast = await PoiDataService.fetchFullForecast(lat, lon, 0, 0, todayStr, state.manifest!, false, model || 'blend', 3);

      const statusAqi = card.querySelector('#aqi-chart-status');
      const statusSmoke = card.querySelector('#smoke-chart-status');
      if (statusAqi) statusAqi.textContent = `Model: ${model.toUpperCase()}`;
      if (statusSmoke) statusSmoke.textContent = `Model: ${model.toUpperCase()}`;

      this.renderCharts(card, forecast);
    } catch (err) {
      console.error('Failed to load station trend forecast:', err);
    }
  }

  private renderCharts(card: HTMLElement, forecast: any) {
    const labels = forecast.timeLabels || [];
    const aqiVals = forecast.aqiValues || [];
    const smokeVals = forecast.smokeDensity || [];

    // Render AQI Chart
    const canvasAqi = card.querySelector('#aqiStationAqiChart') as HTMLCanvasElement;
    if (canvasAqi) {
      const ctx = canvasAqi.getContext('2d');
      if (ctx) {
        this.aqiChart = new Chart(ctx, {
          type: 'line',
          data: {
            labels,
            datasets: [{
              label: 'AQI Forecast',
              data: aqiVals,
              borderColor: '#a855f7',
              backgroundColor: 'rgba(168, 85, 247, 0.15)',
              borderWidth: 2,
              fill: true,
              tension: 0.3,
              pointRadius: 2,
              pointHoverRadius: 5
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx) => `AQI: ${ctx.raw}`
                }
              }
            },
            scales: {
              x: { ticks: { color: '#94a3b8', font: { size: 10 } }, grid: { color: 'rgba(255,255,255,0.05)' } },
              y: { min: 0, max: 200, ticks: { color: '#94a3b8', font: { size: 10 } }, grid: { color: 'rgba(255,255,255,0.05)' } }
            }
          }
        });
      }
    }

    // Render Smoke Chart
    const canvasSmoke = card.querySelector('#aqiStationSmokeChart') as HTMLCanvasElement;
    if (canvasSmoke) {
      const ctx = canvasSmoke.getContext('2d');
      if (ctx) {
        this.smokeChart = new Chart(ctx, {
          type: 'line',
          data: {
            labels,
            datasets: [{
              label: 'Smoke Concentration',
              data: smokeVals,
              borderColor: '#f97316',
              backgroundColor: 'rgba(249, 115, 22, 0.15)',
              borderWidth: 2,
              fill: true,
              tension: 0.3,
              pointRadius: 2,
              pointHoverRadius: 5
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx) => `Smoke: ${ctx.raw} µg/m³`
                }
              }
            },
            scales: {
              x: { ticks: { color: '#94a3b8', font: { size: 10 } }, grid: { color: 'rgba(255,255,255,0.05)' } },
              y: { min: 0, ticks: { color: '#94a3b8', font: { size: 10 } }, grid: { color: 'rgba(255,255,255,0.05)' } }
            }
          }
        });
      }
    }
  }
}

export const aqiStationModal = new AqiStationModal();
